import { faCircleCheck, faHeadset } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortalAuth } from '../../../hooks/usePortalAuth';
import { SupportRequestForm } from '../../SupportRequestForm';

/**
 * Submit-an-Issue widget (PD-13705) — a compact form for signed-in
 * customers to send a categorized question / request / problem report
 * to the support team. The submission is recorded as an Issue in the
 * CRM, linked to the customer's active client and assigned to their
 * sales rep. On success the created issue id is surfaced as a
 * reference number.
 *
 * The form itself is the shared SupportRequestForm (PD-15002) — the
 * Support page's New Support Request modal uses the same component.
 *
 * Login-required — the dashboard keeps it off anonymous layouts via
 * AUTH_ONLY_WIDGET_TYPES; the token guard here is the backstop.
 */
export function SubmitIssueWidget({
	title,
}: {
	title?: string;
}): JSX.Element | null {
	const { t } = useTranslation();
	const { token } = usePortalAuth();
	const navigate = useNavigate();

	const [submittedIssueId, setSubmittedIssueId] = useState<number | null>(null);

	if (token == null) {
		return null;
	}

	return (
		<section className="w-full h-full flex flex-col bg-brand-surface border border-brand-border rounded-xl p-4 md:p-5">
			<header className="flex items-center gap-2 mb-3">
				<FontAwesomeIcon icon={faHeadset} className="text-portal-accent" />
				<h2 className="text-base md:text-lg font-semibold text-default">
					{t(title ?? 'Submit an issue')}
				</h2>
			</header>

			{submittedIssueId != null ? (
				<div className="flex-1 flex flex-col items-center justify-center text-center gap-2 py-6">
					<FontAwesomeIcon
						icon={faCircleCheck}
						className="text-3xl text-portal-accent"
					/>
					<p className="text-sm font-medium text-default">
						{t('Your request has been submitted.')}
					</p>
					<p className="text-xs text-brand-muted">
						{t('Reference')} {t('#')}
						{submittedIssueId}
						{' — '}
						{t('our team will follow up with you.')}
					</p>
					{/* PD-13773 — the submitted issue is trackable on the Support
					    page, so link straight to its thread. */}
					<button
						type="button"
						onClick={() => {
							void navigate(`/support/${submittedIssueId}`);
						}}
						className="mt-2 text-xs md:text-sm text-portal-accent hover:opacity-80 font-medium"
					>
						{t('Track this request')}
					</button>
					<button
						type="button"
						onClick={() => {
							setSubmittedIssueId(null);
						}}
						className="text-xs md:text-sm text-portal-accent hover:opacity-80"
					>
						{t('Submit another issue')}
					</button>
				</div>
			) : (
				<SupportRequestForm onSuccess={setSubmittedIssueId} />
			)}
		</section>
	);
}
